import { useLocalizedPath } from "../i18n/useLocalizedPath";
import React, { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Text,
  useBreakpointValue,
  usePrefersReducedMotion,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import { Autoplay, EffectCoverflow, Pagination } from "swiper/modules";
import {
  FiCalendar,
  FiCamera,
  FiHeart,
  FiShield,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import type { IconType } from "react-icons";
import { normalizeSiteLanguage } from "../i18n/language";
import MaskedReveal from "./MaskedReveal";
import deepSoulLogo from "../deepsoul_logo2.png";

import { PARTNER_IMAGE_URLS } from "../data/newsData";
import udomainLogo from "../images/ud-logo.svg";

const patentInnovationBanner =
  "https://assets.deepsoulai.com/main-page/hero/deepsoul_banner.jpg";
const brandStoryLogo =
  "https://assets.deepsoulai.com/main-page/partnership/partners/brand_story.jpeg";
const helloCocoaLogo =
  "https://assets.deepsoulai.com/main-page/partnership/partners/hello_cocoa.jpeg";
const loveLinkHongKongLogo =
  "https://assets.deepsoulai.com/main-page/partnership/partners/love_link_hong_kong.jpeg";
const wemamaLogo =
  "https://assets.deepsoulai.com/main-page/partnership/partners/wemama.jpeg";
const aiEmotionalSupportCoverImage =
  "https://assets.deepsoulai.com/main-page/product-value/ai-emotional-support/ai_emotional_support_00_cover.png";
const emotionMapCoverImage =
  "https://assets.deepsoulai.com/main-page/product-value/emotion-star-map/emotion_star_map_00_cover.png";
const legacyOfLoveCoverImage =
  "https://assets.deepsoulai.com/main-page/product-value/legacy-of-love/legacy_of_love_00_cover.png";
const dreamImpactCaseImage =
  "https://assets.deepsoulai.com/main-page/partnership/partnership-cases/cases_dream_impact.png";
const chillaxCaseImage =
  "https://assets.deepsoulai.com/main-page/partnership/partnership-cases/cases_chillax.png";
const heyKiddoCaseImage =
  "https://assets.deepsoulai.com/main-page/partnership/partnership-cases/cases_hey_kiddo_child_development_centre.jpg";
const securityLayersImage =
  "https://assets.deepsoulai.com/main-page/trust/security_layers_infographic.png";
const securityAssessmentBadgeImages = {
  en: "https://assets.deepsoulai.com/main-page/trust/ud_security_assessed_en.png",
  zh: "https://assets.deepsoulai.com/main-page/trust/ud_security_assessed_zh_hk.png",
  "zh-CN":
    "https://assets.deepsoulai.com/main-page/trust/ud_security_assessed_zh_hans.png",
} as const;
const pillarExperienceImage =
  "https://assets.deepsoulai.com/main-page/three-pillars/emotional_life_archive.png";
const pillarPlanningImage =
  "https://assets.deepsoulai.com/main-page/three-pillars/life_planning.png";
const pillarExplorationImage =
  "https://assets.deepsoulai.com/main-page/three-pillars/personal_exploration.png";
const founderImage =
  "https://assets.deepsoulai.com/main-page/team/anna_wong.jpeg";
const seniorRDEngImage =
  "https://assets.deepsoulai.com/main-page/team/senior_rd_eng.jpg";
const techLeadImage =
  "https://assets.deepsoulai.com/main-page/team/techlead.jpg";
const personalTraitsCardImage =
  "https://assets.deepsoulai.com/main-page/product-value/personal-traits/personal_traits_00_cover.png";
const personalTraitsEn1 =
  "https://assets.deepsoulai.com/main-page/product-value/personal-traits/personal_traits_01_en.png";
const personalTraitsZh1 =
  "https://assets.deepsoulai.com/main-page/product-value/personal-traits/personal_traits_01_zh_hk.png";
const personalTraitsEn2 =
  "https://assets.deepsoulai.com/main-page/product-value/personal-traits/personal_traits_02_en.png";
const personalTraitsZh2 =
  "https://assets.deepsoulai.com/main-page/product-value/personal-traits/personal_traits_02_zh_hk.png";
const personalTraitsEn3 =
  "https://assets.deepsoulai.com/main-page/product-value/personal-traits/personal_traits_03_en.png";
const personalTraitsZh3 =
  "https://assets.deepsoulai.com/main-page/product-value/personal-traits/personal_traits_03_zh_hk.png";
const messageForYouCardImage =
  "https://assets.deepsoulai.com/main-page/product-value/message-for-you/message_for_you_00_cover.png";
const messageForYouEn1 =
  "https://assets.deepsoulai.com/main-page/product-value/message-for-you/message_for_you_01_en.png";
const messageForYouZh1 =
  "https://assets.deepsoulai.com/main-page/product-value/message-for-you/message_for_you_01_zh_hk.png";
const messageForYouEn2 =
  "https://assets.deepsoulai.com/main-page/product-value/message-for-you/message_for_you_02_en.png";
const messageForYouZh2 =
  "https://assets.deepsoulai.com/main-page/product-value/message-for-you/message_for_you_02_zh_hk.png";
const messageForYouEn3 =
  "https://assets.deepsoulai.com/main-page/product-value/message-for-you/message_for_you_03_en.png";
const messageForYouZh3 =
  "https://assets.deepsoulai.com/main-page/product-value/message-for-you/message_for_you_03_zh_hk.png";
const lifeTrajectoriesCardImage =
  "https://assets.deepsoulai.com/main-page/product-value/life-trajectories/life_trajectories_00_cover.png";
const lifeTrajectoriesEn1 =
  "https://assets.deepsoulai.com/main-page/product-value/life-trajectories/life_trajectories_01_en.png";
const lifeTrajectoriesZh1 =
  "https://assets.deepsoulai.com/main-page/product-value/life-trajectories/life_trajectories_01_zh_hk.png";
const lifeTrajectoriesEn2 =
  "https://assets.deepsoulai.com/main-page/product-value/life-trajectories/life_trajectories_02_en.png";
const lifeTrajectoriesZh2 =
  "https://assets.deepsoulai.com/main-page/product-value/life-trajectories/life_trajectories_02_zh_hk.png";
const lifeTrajectoriesEn3 =
  "https://assets.deepsoulai.com/main-page/product-value/life-trajectories/life_trajectories_03_en.png";
const lifeTrajectoriesZh3 =
  "https://assets.deepsoulai.com/main-page/product-value/life-trajectories/life_trajectories_03_zh_hk.png";
const lifeMomentsCardImage =
  "https://assets.deepsoulai.com/main-page/product-value/life-moments/life_moments_00_cover.png";
const lifeMomentsEn1 =
  "https://assets.deepsoulai.com/main-page/product-value/life-moments/life_moments_01_en.png";
const lifeMomentsZh1 =
  "https://assets.deepsoulai.com/main-page/product-value/life-moments/life_moments_01_zh_hk.png";
const lifeMomentsEn2 =
  "https://assets.deepsoulai.com/main-page/product-value/life-moments/life_moments_02_en.png";
const lifeMomentsZh2 =
  "https://assets.deepsoulai.com/main-page/product-value/life-moments/life_moments_02_zh_hk.png";
const lifeMomentsEn3 =
  "https://assets.deepsoulai.com/main-page/product-value/life-moments/life_moments_03_en.png";
const lifeMomentsZh3 =
  "https://assets.deepsoulai.com/main-page/product-value/life-moments/life_moments_03_zh_hk.png";
const yourDreamCardImage =
  "https://assets.deepsoulai.com/main-page/product-value/ideal-dream/ideal_dream_00_cover.png";
const yourDreamEn1 =
  "https://assets.deepsoulai.com/main-page/product-value/ideal-dream/ideal_dream_01_en.png";
const yourDreamZh1 =
  "https://assets.deepsoulai.com/main-page/product-value/ideal-dream/ideal_dream_01_zh_hk.png";
const yourDreamEn2 =
  "https://assets.deepsoulai.com/main-page/product-value/ideal-dream/ideal_dream_02_en.png";
const yourDreamZh2 =
  "https://assets.deepsoulai.com/main-page/product-value/ideal-dream/ideal_dream_02_zh_hk.png";
const yourDreamEn3 =
  "https://assets.deepsoulai.com/main-page/product-value/ideal-dream/ideal_dream_03_en.png";
const yourDreamZh3 =
  "https://assets.deepsoulai.com/main-page/product-value/ideal-dream/ideal_dream_03_zh_hk.png";
const hexacoReportCardImage =
  "https://assets.deepsoulai.com/main-page/product-value/hexaco-report/hexaco_report_00_cover.png";
const hexacoReportEn1 =
  "https://assets.deepsoulai.com/main-page/product-value/hexaco-report/hexaco_report_01_en.png";
const hexacoReportZh1 =
  "https://assets.deepsoulai.com/main-page/product-value/hexaco-report/hexaco_report_01_zh_hk.png";
const hexacoReportEn2 =
  "https://assets.deepsoulai.com/main-page/product-value/hexaco-report/hexaco_report_02_en.png";
const hexacoReportZh2 =
  "https://assets.deepsoulai.com/main-page/product-value/hexaco-report/hexaco_report_02_zh_hk.png";
const hexacoReportEn3 =
  "https://assets.deepsoulai.com/main-page/product-value/hexaco-report/hexaco_report_03_en.png";
const hexacoReportZh3 =
  "https://assets.deepsoulai.com/main-page/product-value/hexaco-report/hexaco_report_03_zh_hk.png";
const aiWritingAssistantCardImage =
  "https://assets.deepsoulai.com/main-page/product-value/ai-writing-assistant/ai_writing_assistant_00_cover.png";
const aiWritingAssistantEn1 =
  "https://assets.deepsoulai.com/main-page/product-value/ai-writing-assistant/ai_writing_assistant_01_en.png";
const aiWritingAssistantZh1 =
  "https://assets.deepsoulai.com/main-page/product-value/ai-writing-assistant/ai_writing_assistant_01_zh_hk.png";
const aiWritingAssistantEn2 =
  "https://assets.deepsoulai.com/main-page/product-value/ai-writing-assistant/ai_writing_assistant_02_en.png";
const aiWritingAssistantZh2 =
  "https://assets.deepsoulai.com/main-page/product-value/ai-writing-assistant/ai_writing_assistant_02_zh_hk.png";
const aiWritingAssistantEn3 =
  "https://assets.deepsoulai.com/main-page/product-value/ai-writing-assistant/ai_writing_assistant_03_en.png";
const aiWritingAssistantZh3 =
  "https://assets.deepsoulai.com/main-page/product-value/ai-writing-assistant/ai_writing_assistant_03_zh_hk.png";
const hkfiiImage =
  "https://assets.deepsoulai.com/main-page/partnership/scenes/hkfii_expanded.png";
const esgRecognitionImage =
  "https://assets.deepsoulai.com/about-company-story/award_achievement.jpg";
const innoLinkRecognitionImage =
  "https://appconfig.deepsoulai.com/assests/q2_innolink_1.jpg";
const seminarImage =
  "https://assets.deepsoulai.com/main-page/partnership/scenes/seminar_preserved.jpg";

type SectionHeadingProps = {
  number: string;
  title: string;
  statement?: string;
  reversed?: boolean;
};

type ProductItem = {
  key: string;
  cardImage?: string;
  detailImages?: string[];
  hasDescription?: boolean;
};

type PartnershipCase = {
  key: "dreamImpact" | "chillax" | "heyKiddo";
  image: string;
};

const partnershipCases: PartnershipCase[] = [
  { key: "dreamImpact", image: dreamImpactCaseImage },
  { key: "chillax", image: chillaxCaseImage },
  { key: "heyKiddo", image: heyKiddoCaseImage },
];

type AudienceKey =
  | "selfGrowth"
  | "memories"
  | "wellbeing"
  | "planning"
  | "legacy"
  | "privacy";

type AudienceItem = {
  key: AudienceKey;
  icon: IconType;
  orbitPosition: {
    top: string;
    side: "left" | "right";
  };
  mobilePosition: {
    top: string;
    side: "left" | "right";
  };
};

const audienceItems: AudienceItem[] = [
  {
    key: "selfGrowth",
    icon: FiUser,
    orbitPosition: { top: "8%", side: "left" },
    mobilePosition: { top: "4%", side: "left" },
  },
  {
    key: "memories",
    icon: FiCamera,
    orbitPosition: { top: "42%", side: "left" },
    mobilePosition: { top: "41%", side: "left" },
  },
  {
    key: "wellbeing",
    icon: FiHeart,
    orbitPosition: { top: "76%", side: "left" },
    mobilePosition: { top: "78%", side: "left" },
  },
  {
    key: "planning",
    icon: FiCalendar,
    orbitPosition: { top: "8%", side: "right" },
    mobilePosition: { top: "4%", side: "right" },
  },
  {
    key: "legacy",
    icon: FiUsers,
    orbitPosition: { top: "42%", side: "right" },
    mobilePosition: { top: "41%", side: "right" },
  },
  {
    key: "privacy",
    icon: FiShield,
    orbitPosition: { top: "76%", side: "right" },
    mobilePosition: { top: "78%", side: "right" },
  },
];

const audienceConnectorPaths: Record<AudienceKey, string> = {
  selfGrowth: "M 210 95 C 320 105, 365 170, 430 220",
  memories: "M 210 310 C 320 310, 365 310, 430 310",
  wellbeing: "M 210 525 C 320 515, 365 450, 430 400",
  planning: "M 990 95 C 880 105, 835 170, 770 220",
  legacy: "M 990 310 C 880 310, 835 310, 770 310",
  privacy: "M 990 525 C 880 515, 835 450, 770 400",
};

const mobileAudienceConnectorPaths: Record<AudienceKey, string> = {
  selfGrowth: "M 42 62 C 82 78, 92 132, 112 164",
  memories: "M 42 270 C 72 270, 88 270, 104 270",
  wellbeing: "M 42 478 C 82 462, 92 408, 112 376",
  planning: "M 318 62 C 278 78, 268 132, 248 164",
  legacy: "M 318 270 C 288 270, 272 270, 256 270",
  privacy: "M 318 478 C 278 462, 268 408, 248 376",
};

const SectionHeading = ({
  number,
  title,
  statement,
  reversed = false,
}: SectionHeadingProps) => (
  <Grid
    data-section-number={number}
    templateColumns={{
      base: "1fr",
      lg: statement ? "0.7fr 1.3fr" : "1fr",
    }}
    gap={{ base: 5, lg: 14 }}
    alignItems="start"
    mb={{ base: 9, md: 14 }}
  >
    <Flex
      gridColumn={{ lg: reversed ? 2 : 1 }}
      gridRow={{ lg: 1 }}
      justify={{ lg: reversed ? "flex-end" : "flex-start" }}
      align="center"
      gap={3}
      color="gray.500"
      letterSpacing={statement ? "0.12em" : "-0.04em"}
      textTransform={statement ? "uppercase" : "none"}
    >
      <Text color="#25272D" fontWeight="700">
        {number}
      </Text>
      <Heading
        as="h2"
        fontSize={
          statement ? { base: "sm", md: "md" } : { base: "3xl", md: "5xl" }
        }
        lineHeight={statement ? undefined : { base: 1.3, md: 1.16 }}
        letterSpacing={statement ? "0.12em" : "-0.04em"}
      >
        {title}
      </Heading>
    </Flex>
    {statement && (
      <Heading
        gridColumn={{ lg: reversed ? 1 : 2 }}
        gridRow={{ lg: 1 }}
        as="h3"
        fontSize={{ base: "3xl", md: "5xl" }}
        lineHeight={{ base: 1.3, md: 1.16 }}
        letterSpacing="-0.04em"
        maxW={{ base: "100%", md: "21ch" }}
        sx={{ textWrap: "balance" }}
      >
        {statement}
      </Heading>
    )}
  </Grid>
);

const SectionShell = ({
  id,
  children,
  tinted = false,
}: {
  id: string;
  children: React.ReactNode;
  tinted?: boolean;
}) => (
  <Box
    as="section"
    id={id}
    data-testid={`v2-section-${id}`}
    scrollMarginTop="88px"
    bg={
      tinted
        ? "linear-gradient(180deg, rgba(246,249,252,.92), rgba(255,244,246,.72))"
        : "white"
    }
  >
    <Box
      maxW="1400px"
      mx="auto"
      px={{ base: 4, md: 8 }}
      py={{ base: 14, md: 24 }}
    >
      {children}
    </Box>
  </Box>
);

const HomeV2Sections: React.FC = () => {
  const { t, i18n } = useTranslation();
  const localizePath = useLocalizedPath();
  const [selectedProductKey, setSelectedProductKey] = useState<string | null>(
    null,
  );
  const [selectedProductImageIndex, setSelectedProductImageIndex] = useState(0);
  const [selectedPartnershipCaseIndex, setSelectedPartnershipCaseIndex] =
    useState<number | null>(null);
  const [activePillarKey, setActivePillarKey] = useState<string | null>(null);
  const [selectedAudienceKey, setSelectedAudienceKey] =
    useState<AudienceKey>("selfGrowth");
  const prefersReducedMotion = usePrefersReducedMotion();
  const siteLanguage = normalizeSiteLanguage(
    i18n.resolvedLanguage || i18n.language,
  );
  const isChinese = siteLanguage !== "en";
  const securityAssessmentBadgeImage =
    securityAssessmentBadgeImages[siteLanguage];
  const productItems: ProductItem[] = isChinese
    ? [
        {
          key: "lifeMoments",
          cardImage: lifeMomentsCardImage,
          detailImages: [lifeMomentsZh1, lifeMomentsZh2, lifeMomentsZh3],
        },
        {
          key: "lifeTrajectories",
          cardImage: lifeTrajectoriesCardImage,
          detailImages: [
            lifeTrajectoriesZh1,
            lifeTrajectoriesZh2,
            lifeTrajectoriesZh3,
          ],
        },
        {
          key: "emotionMap",
          cardImage: emotionMapCoverImage,
        },
        {
          key: "personalTraits",
          cardImage: personalTraitsCardImage,
          detailImages: [
            personalTraitsZh1,
            personalTraitsZh2,
            personalTraitsZh3,
          ],
        },
        {
          key: "yourDream",
          cardImage: yourDreamCardImage,
          detailImages: [yourDreamZh1, yourDreamZh2, yourDreamZh3],
        },
        {
          key: "messageForYou",
          cardImage: messageForYouCardImage,
          detailImages: [messageForYouZh1, messageForYouZh2, messageForYouZh3],
        },
        {
          key: "legacyOfLove",
          cardImage: legacyOfLoveCoverImage,
        },
        {
          key: "hexacoReport",
          cardImage: hexacoReportCardImage,
          detailImages: [hexacoReportZh1, hexacoReportZh2, hexacoReportZh3],
        },
        {
          key: "aiWritingAssistant",
          cardImage: aiWritingAssistantCardImage,
          detailImages: [
            aiWritingAssistantZh1,
            aiWritingAssistantZh2,
            aiWritingAssistantZh3,
          ],
        },
        {
          key: "souli",
          cardImage: aiEmotionalSupportCoverImage,
        },
      ]
    : [
        {
          key: "lifeMoments",
          cardImage: lifeMomentsCardImage,
          detailImages: [lifeMomentsEn1, lifeMomentsEn2, lifeMomentsEn3],
        },
        {
          key: "lifeTrajectories",
          cardImage: lifeTrajectoriesCardImage,
          detailImages: [
            lifeTrajectoriesEn1,
            lifeTrajectoriesEn2,
            lifeTrajectoriesEn3,
          ],
        },
        {
          key: "emotionMap",
          cardImage: emotionMapCoverImage,
        },
        {
          key: "personalTraits",
          cardImage: personalTraitsCardImage,
          detailImages: [
            personalTraitsEn1,
            personalTraitsEn2,
            personalTraitsEn3,
          ],
        },
        {
          key: "yourDream",
          cardImage: yourDreamCardImage,
          detailImages: [yourDreamEn1, yourDreamEn2, yourDreamEn3],
        },
        {
          key: "messageForYou",
          cardImage: messageForYouCardImage,
          detailImages: [messageForYouEn1, messageForYouEn2, messageForYouEn3],
        },
        {
          key: "legacyOfLove",
          cardImage: legacyOfLoveCoverImage,
        },
        {
          key: "hexacoReport",
          cardImage: hexacoReportCardImage,
          detailImages: [hexacoReportEn1, hexacoReportEn2, hexacoReportEn3],
        },
        {
          key: "aiWritingAssistant",
          cardImage: aiWritingAssistantCardImage,
          detailImages: [
            aiWritingAssistantEn1,
            aiWritingAssistantEn2,
            aiWritingAssistantEn3,
          ],
        },
        {
          key: "souli",
          cardImage: aiEmotionalSupportCoverImage,
        },
      ];
  const pillarItems = [
    { key: "experience", image: pillarExperienceImage },
    { key: "exploration", image: pillarExplorationImage },
    { key: "planning", image: pillarPlanningImage },
  ];
  const trustItems = ["enterprise", "sensitive", "ai", "assessment"];
  const partnerItems = [
    {
      key: "hkper",
      image: PARTNER_IMAGE_URLS.hkper,
      logoScale: 0.65,
    },
    { key: "wemama", image: wemamaLogo, logoScale: 0.8 },
    { key: "loveLinkHongKong", image: loveLinkHongKongLogo, logoScale: 0.85 },
    { key: "brandStory", image: brandStoryLogo, logoScale: 0.85 },
    { key: "helloCocoa", image: helloCocoaLogo, logoScale: 1.85 },

    {
      key: "dreamImpact",
      image: PARTNER_IMAGE_URLS.dreamImpact,
      logoScale: 0.65,
    },
    {
      key: "chillax",
      image: PARTNER_IMAGE_URLS.chillax,
      logoScale: 2,
    },
    {
      key: "heyKiddo",
      image: PARTNER_IMAGE_URLS.heyKiddo,
      logoScale: 0.95,
    },
    {
      key: "aimHeart",
      image: PARTNER_IMAGE_URLS.aimHeart,
      logoScale: 0.65,
    },
    { key: "udomain", image: PARTNER_IMAGE_URLS.udomain, logoScale: 1.35 },
  ];
  const partnershipContexts = [
    { key: "innovation", image: hkfiiImage },
    { key: "community", image: seminarImage, fullBleed: true },
  ];
  const recognitionItems = [
    { key: "hkfii", image: hkfiiImage, objectFit: "contain" },
    { key: "esg", image: esgRecognitionImage, objectFit: "cover" },
    { key: "innoLink", image: innoLinkRecognitionImage, objectFit: "cover" },
  ] as const;
  const teamItems = [
    {
      key: "founder",
      image: founderImage,
      descriptionKey: "founder.description",
    },
    {
      key: "techLead",
      image: techLeadImage,
      descriptionKey: "techLead.description",
    },
    {
      key: "senior",
      image: seniorRDEngImage,
      descriptionKey: "seniorRDEng.description",
    },
  ];
  const showDesktopCapabilities =
    useBreakpointValue({ base: false, md: true }) ?? false;
  const selectedProduct =
    productItems.find(({ key }) => key === selectedProductKey) || null;
  const selectedProductImages = selectedProduct?.detailImages ?? [];
  const selectedProductImage =
    selectedProductImages[selectedProductImageIndex] ?? null;
  const selectedProductHasDescription =
    selectedProduct?.hasDescription !== false;
  const closeProductModal = () => {
    setSelectedProductKey(null);
    setSelectedProductImageIndex(0);
  };
  const selectProduct = (key: string) => {
    setSelectedProductImageIndex(0);
    setSelectedProductKey(key);
  };
  const showPreviousProductImage = () => {
    setSelectedProductImageIndex((current) =>
      current === 0 ? selectedProductImages.length - 1 : current - 1,
    );
  };
  const showNextProductImage = () => {
    setSelectedProductImageIndex((current) =>
      current === selectedProductImages.length - 1 ? 0 : current + 1,
    );
  };
  const getTrustSummary = (key: string) =>
    t(`v2Home.trust.items.${key}.summary`, {
      defaultValue: t(`v2Home.trust.items.${key}.description`),
    });

  const selectedAudience =
    audienceItems.find(({ key }) => key === selectedAudienceKey) ??
    audienceItems[0];

  const selectAudience = (key: AudienceKey) => {
    setSelectedAudienceKey(key);
  };
  const selectedPartnershipCase =
    partnershipCases[selectedPartnershipCaseIndex ?? 0];
  const closePartnershipCaseModal = () => setSelectedPartnershipCaseIndex(null);
  const showPreviousPartnershipCase = () =>
    setSelectedPartnershipCaseIndex((current) =>
      current === null
        ? 0
        : (current - 1 + partnershipCases.length) % partnershipCases.length,
    );
  const showNextPartnershipCase = () =>
    setSelectedPartnershipCaseIndex((current) =>
      current === null ? 0 : (current + 1) % partnershipCases.length,
    );

  const renderAudienceButton = (
    { key, icon: Icon }: AudienceItem,
    variant: "orbit" | "grid" | "mobile" = "grid",
  ) => {
    const isSelected = selectedAudienceKey === key;
    const isCompact = variant !== "grid";

    return (
      <Box
        key={key}
        position="relative"
        w={
          variant === "orbit" ? "220px" : variant === "mobile" ? "auto" : "100%"
        }
      >
        <Button
          data-testid={`audience-option-${key}`}
          data-audience-icon={key}
          type="button"
          aria-pressed={isSelected}
          onClick={() => selectAudience(key)}
          w="100%"
          minW={variant === "mobile" ? "220px" : "0"}
          h="auto"
          minH={isCompact ? "82px" : "104px"}
          p={isCompact ? 4 : 5}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap={3}
          whiteSpace="normal"
          textAlign="left"
          border="1px solid"
          borderColor={isSelected ? "primary.400" : "blackAlpha.200"}
          borderRadius={isCompact ? "full" : "2xl"}
          bg={isSelected ? "rgba(255, 63, 79, 0.07)" : "white"}
          color="gray.800"
          boxShadow={
            isSelected
              ? "0 0 0 4px rgba(255,63,79,.07), 0 14px 34px rgba(255,63,79,.14)"
              : "0 10px 28px rgba(74,76,92,.07)"
          }
          transition={
            prefersReducedMotion
              ? "none"
              : "border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease"
          }
          _hover={{
            borderColor: "primary.300",
            bg: "rgba(255, 63, 79, 0.045)",
            transform: prefersReducedMotion ? "none" : "translateY(-2px)",
          }}
          _focusVisible={{
            outline: "3px solid",
            outlineColor: "primary.400",
            outlineOffset: "3px",
          }}
        >
          <Flex
            flex="0 0 auto"
            w="42px"
            h="42px"
            align="center"
            justify="center"
            borderRadius="full"
            bg={isSelected ? "primary.500" : "rgba(255, 63, 79, 0.1)"}
            color={isSelected ? "white" : "primary.500"}
          >
            {React.createElement(Icon as React.ComponentType<any>, {
              "data-testid": `audience-icon-${key}`,
              size: 21,
              "aria-hidden": "true",
            })}
          </Flex>
          <Box minW={0}>
            <Text fontWeight="800" lineHeight="1.4">
              {t(`v2Home.audience.items.${key}.title`)}
            </Text>
          </Box>
        </Button>
      </Box>
    );
  };

  const renderAudienceDetail = (compact = false) => {
    const SelectedIcon = selectedAudience.icon;

    return (
      <Flex
        data-testid="audience-detail"
        minH={compact ? "330px" : "100%"}
        px={{ base: 6, md: 8 }}
        py={{ base: 8, md: 10 }}
        position="relative"
        overflow="hidden"
        direction="column"
        align="center"
        justify="center"
        textAlign="center"
        border="1px solid"
        borderColor="whiteAlpha.900"
        borderRadius="3xl"
        bg="linear-gradient(150deg, rgba(255,255,255,.96), rgba(255,240,244,.92) 55%, rgba(238,247,255,.94))"
        boxShadow="0 24px 64px rgba(74, 76, 92, .12)"
        transition={prefersReducedMotion ? "none" : "box-shadow 180ms ease"}
      >
        <Flex position="relative" zIndex={1} align="center" gap={3} mb={6}>
          <Image
            src={deepSoulLogo}
            alt="Deep Soul"
            boxSize="54px"
            objectFit="contain"
          />
          <Text color="primary.600" fontSize="xl" fontWeight="800">
            Deep Soul
          </Text>
        </Flex>
        <Flex
          data-testid={`audience-detail-icon-${selectedAudience.key}`}
          position="relative"
          zIndex={1}
          w="68px"
          h="68px"
          mb={5}
          align="center"
          justify="center"
          borderRadius="full"
          bg="primary.500"
          color="white"
          boxShadow="0 12px 30px rgba(255,63,79,.25)"
        >
          {React.createElement(SelectedIcon as React.ComponentType<any>, {
            size: 30,
            "aria-hidden": "true",
          })}
        </Flex>
        <Heading
          data-testid="audience-detail-title"
          position="relative"
          zIndex={1}
          as="h3"
          fontSize={{ base: "xl", md: "2xl" }}
          mb={3}
        >
          {t(`v2Home.audience.items.${selectedAudience.key}.title`)}
        </Heading>
        <Text
          data-testid="audience-detail-description"
          position="relative"
          zIndex={1}
          maxW="38ch"
          color="gray.600"
          lineHeight="1.8"
        >
          {t(`v2Home.audience.items.${selectedAudience.key}.description`)}
        </Text>
        <Box
          position="relative"
          zIndex={1}
          w="44px"
          h="2px"
          my={6}
          bg="primary.400"
        />
        <Text
          data-testid="audience-conclusion"
          position="relative"
          zIndex={1}
          maxW="34ch"
          color="gray.800"
          fontWeight="700"
          lineHeight="1.75"
        >
          {t("v2Home.audience.conclusion")}
        </Text>
      </Flex>
    );
  };

  const renderMobileAudienceNode = ({ key, icon: Icon }: AudienceItem) => {
    const isSelected = selectedAudienceKey === key;

    return (
      <Button
        data-testid={`audience-mobile-option-${key}`}
        type="button"
        aria-pressed={isSelected}
        aria-label={t(`v2Home.audience.items.${key}.title`)}
        onClick={() => selectAudience(key)}
        w="76px"
        h="auto"
        p={0}
        display="flex"
        flexDirection="column"
        gap={2}
        color={isSelected ? "primary.600" : "gray.600"}
        bg="transparent"
        whiteSpace="normal"
        _hover={{ bg: "transparent" }}
        _focusVisible={{
          outline: "3px solid",
          outlineColor: "primary.400",
          outlineOffset: "3px",
        }}
      >
        <Flex
          data-testid={`audience-mobile-icon-${key}`}
          w={isSelected ? "62px" : "56px"}
          h={isSelected ? "62px" : "56px"}
          align="center"
          justify="center"
          borderRadius="full"
          border="1px solid"
          borderWidth={isSelected ? "2px" : "1px"}
          borderColor={isSelected ? "primary.400" : "blackAlpha.200"}
          bg={isSelected ? "rgba(255, 63, 79, .08)" : "white"}
          color={isSelected ? "primary.500" : "primary.300"}
          boxShadow={
            isSelected
              ? "0 0 0 6px rgba(255,63,79,.07), 0 10px 24px rgba(255,63,79,.17)"
              : "0 8px 20px rgba(74,76,92,.08)"
          }
          transition={
            prefersReducedMotion
              ? "none"
              : "width 180ms ease, height 180ms ease, border-color 180ms ease, box-shadow 180ms ease"
          }
        >
          {React.createElement(Icon as React.ComponentType<any>, {
            size: 23,
            "aria-hidden": "true",
          })}
        </Flex>
        <Text fontSize="xs" fontWeight="800" lineHeight="1.3">
          {t(`v2Home.audience.items.${key}.shortTitle`)}
        </Text>
      </Button>
    );
  };

  const renderMobileAudienceDetail = () => {
    const SelectedIcon = selectedAudience.icon;

    return (
      <Flex
        data-testid="audience-mobile-detail"
        position="absolute"
        left="50%"
        top="50%"
        transform="translate(-50%, -50%)"
        zIndex={1}
        w="calc(100% - 140px)"
        maxW="210px"
        minH="286px"
        px={2}
        py={5}
        overflow="hidden"
        direction="column"
        align="center"
        justify="center"
        textAlign="center"
        border="1px solid"
        borderColor="whiteAlpha.900"
        borderRadius="3xl"
        bg="linear-gradient(150deg, rgba(255,255,255,.97), rgba(255,240,244,.93) 55%, rgba(238,247,255,.95))"
        boxShadow="0 18px 44px rgba(74,76,92,.13)"
        sx={{
          "@media (max-width: 360px)": {
            minHeight: "252px",
            paddingTop: "14px",
            paddingBottom: "14px",
          },
        }}
      >
        <Flex position="relative" zIndex={1} align="center" gap={2} mb={4}>
          <Image
            src={deepSoulLogo}
            alt="Deep Soul"
            boxSize="38px"
            objectFit="contain"
          />
          <Text color="primary.600" fontSize="md" fontWeight="800">
            Deep Soul
          </Text>
        </Flex>
        <Flex
          data-testid={`audience-mobile-detail-icon-${selectedAudience.key}`}
          position="relative"
          zIndex={1}
          w="44px"
          h="44px"
          mb={3}
          align="center"
          justify="center"
          borderRadius="full"
          bg="primary.500"
          color="white"
          boxShadow="0 9px 24px rgba(255,63,79,.24)"
        >
          {React.createElement(SelectedIcon as React.ComponentType<any>, {
            size: 21,
            "aria-hidden": "true",
          })}
        </Flex>
        <Heading
          data-testid="audience-mobile-detail-title"
          data-mobile-text-wrap="anywhere"
          position="relative"
          zIndex={1}
          as="h3"
          w="100%"
          maxW="100%"
          fontSize="14px"
          lineHeight="1.35"
          whiteSpace="normal"
          overflowWrap="anywhere"
          wordBreak="break-word"
          mb={2}
          sx={{ "@media (max-width: 360px)": { fontSize: "13px" } }}
        >
          {t(`v2Home.audience.items.${selectedAudience.key}.title`)}
        </Heading>
        <Text
          data-testid="audience-mobile-detail-description"
          data-mobile-text-wrap="anywhere"
          position="relative"
          zIndex={1}
          w="100%"
          maxW="100%"
          color="gray.600"
          fontSize="xs"
          lineHeight="1.65"
          whiteSpace="normal"
          overflowWrap="anywhere"
          wordBreak="break-word"
        >
          {t(`v2Home.audience.items.${selectedAudience.key}.description`)}
        </Text>
        <Text
          data-testid="audience-mobile-conclusion-inline"
          data-mobile-text-wrap="anywhere"
          position="relative"
          zIndex={1}
          w="100%"
          maxW="100%"
          mt={4}
          pt={3}
          borderTop="1px solid"
          borderColor="blackAlpha.100"
          color="gray.800"
          fontSize="11px"
          fontWeight="600"
          lineHeight="1.55"
          whiteSpace="normal"
          overflowWrap="anywhere"
          wordBreak="break-word"
          sx={{ "@media (max-width: 360px)": { display: "none" } }}
        >
          {t("v2Home.audience.conclusion")}
        </Text>
      </Flex>
    );
  };

  const renderProductCard = (
    { key, cardImage }: (typeof productItems)[number],
    index: number,
  ) => {
    return (
      <MaskedReveal key={key} delay={index * 0.05}>
        <Box
          as="button"
          type="button"
          data-testid={`product-capability-${key}`}
          aria-haspopup="dialog"
          aria-controls="product-capability-dialog"
          w="100%"
          minH={{ base: "250px", md: "280px" }}
          p={0}
          textAlign="left"
          border="1px solid"
          borderColor="blackAlpha.200"
          borderRadius="2xl"
          overflow="hidden"
          bg="white"
          onClick={() => selectProduct(key)}
          transition="border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease"
          _hover={{ borderColor: "primary.300", transform: "translateY(-2px)" }}
          _focusVisible={{
            outline: "3px solid",
            outlineColor: "primary.400",
            outlineOffset: "4px",
          }}
        >
          <Box
            data-testid={`product-card-image-frame-${key}`}
            data-aspect-ratio="16:9"
            aspectRatio="16 / 9"
            overflow="hidden"
            bg="linear-gradient(145deg, #fff8ef 0%, #f6f2ff 52%, #eef8ff 100%)"
            p={{ base: 3, md: 4 }}
          >
            {cardImage && (
              <Image
                data-testid={`product-card-image-${key}`}
                src={cardImage}
                alt={t(`v2Home.why.items.${key}.imageAlt`)}
                w="100%"
                h="100%"
                objectFit="contain"
                objectPosition="center"
              />
            )}
          </Box>
          <Box p={{ base: 5, md: 6 }}>
            <Heading
              as="h4"
              fontSize={{ base: "lg", md: "xl" }}
              lineHeight="1.35"
            >
              {t(`v2Home.why.items.${key}.title`)}
            </Heading>
          </Box>
        </Box>
        {(key === "lifeMoments" || key === "aiWritingAssistant") && (
          <Link
            as={RouterLink}
            to={localizePath("/diary-app")}
            display="inline-block"
            mt={3}
            px={2}
            py={2}
            color="primary.700"
            fontWeight="600"
            textDecoration="underline"
            textUnderlineOffset="4px"
            _hover={{ color: "primary.800" }}
            _focusVisible={{
              outline: "3px solid",
              outlineColor: "primary.400",
              outlineOffset: "3px",
              borderRadius: "md",
            }}
          >
            {t(key === "lifeMoments" ? "v2Home.why.diaryLink" : "v2Home.why.aiDiaryLink")}
          </Link>
        )}
      </MaskedReveal>
    );
  };

  return (
    <>
      <SectionShell id="three-pillars" tinted>
        <MaskedReveal>
          <SectionHeading
            number="02"
            title={t("v2Home.pillars.title")}
            statement={t("v2Home.pillars.statement")}
          />
        </MaskedReveal>
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={6}
          alignItems="stretch"
        >
          {pillarItems.map(({ key, image }, index) => {
            const isActive = activePillarKey === key;
            const detailId = `pillar-detail-${key}`;

            return (
              <MaskedReveal key={key} delay={index * 0.08}>
                <Box
                  as="button"
                  type="button"
                  data-testid={`pillar-card-${key}`}
                  aria-expanded={isActive}
                  aria-controls={detailId}
                  w="100%"
                  h="100%"
                  minH={{ base: "260px", md: "320px" }}
                  p={0}
                  display="block"
                  boxSizing="border-box"
                  appearance="none"
                  fontFamily="inherit"
                  verticalAlign="top"
                  position="relative"
                  overflow="hidden"
                  textAlign="left"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="whiteAlpha.800"
                  bg="rgba(255,255,255,.84)"
                  boxShadow="0 18px 50px rgba(72,84,96,.08)"
                  cursor="pointer"
                  onMouseEnter={() => {
                    if (showDesktopCapabilities) setActivePillarKey(key);
                  }}
                  onMouseLeave={(event) => {
                    if (
                      showDesktopCapabilities &&
                      document.activeElement !== event.currentTarget
                    ) {
                      setActivePillarKey(null);
                    }
                  }}
                  onFocus={() => {
                    if (showDesktopCapabilities) setActivePillarKey(key);
                  }}
                  onBlur={() => {
                    if (showDesktopCapabilities) setActivePillarKey(null);
                  }}
                  onClick={() => {
                    if (!showDesktopCapabilities) {
                      setActivePillarKey((current) =>
                        current === key ? null : key,
                      );
                    }
                  }}
                  _hover={{ boxShadow: "0 22px 58px rgba(72,84,96,.14)" }}
                  _focusVisible={{
                    outline: "3px solid",
                    outlineColor: "primary.400",
                    outlineOffset: "4px",
                  }}
                >
                  <Box
                    data-testid={`pillar-image-frame-${key}`}
                    data-aspect-ratio="16:9"
                    aspectRatio="16 / 9"
                    overflow="hidden"
                    bg="gray.50"
                  >
                    <Image
                      data-testid={`pillar-image-${key}`}
                      src={image}
                      alt=""
                      aria-hidden="true"
                      w="100%"
                      h="100%"
                      objectFit="contain"
                      objectPosition="center"
                    />
                  </Box>
                  <Box p={{ base: 6, md: 7 }}>
                    <Text color="primary.500" fontWeight="700" mb={3}>
                      0{index + 1}
                    </Text>
                    <Heading as="h4" fontSize="2xl">
                      {t(`v2Home.pillars.items.${key}.title`)}
                    </Heading>
                  </Box>

                  <Box
                    id={detailId}
                    data-testid={`pillar-overlay-${key}`}
                    data-active={isActive ? "true" : "false"}
                    data-motion={prefersReducedMotion ? "none" : "fade"}
                    aria-hidden={!isActive}
                    position="absolute"
                    inset={0}
                    zIndex={1}
                    p={{ base: 7, md: 9 }}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    bg="linear-gradient(145deg, rgba(255,244,247,.98), rgba(235,247,255,.98))"
                    opacity={isActive ? 1 : 0}
                    pointerEvents="none"
                    transition={
                      prefersReducedMotion ? "none" : "opacity 180ms ease"
                    }
                  >
                    <Text color="primary.600" fontWeight="700" mb={3}>
                      0{index + 1}
                    </Text>
                    <Heading
                      as="h4"
                      fontSize={{ base: "xl", md: "2xl" }}
                      mb={5}
                    >
                      {t(`v2Home.pillars.items.${key}.title`)}
                    </Heading>
                    <Text color="gray.700" lineHeight="1.8">
                      {t(`v2Home.pillars.items.${key}.description`)}
                    </Text>
                  </Box>
                </Box>
              </MaskedReveal>
            );
          })}
        </SimpleGrid>
      </SectionShell>

      <SectionShell id="why-deep-soul">
        <Grid
          templateColumns={{ base: "1fr", lg: "0.7fr 1.3fr" }}
          gap={{ base: 9, lg: 16 }}
          alignItems="start"
        >
          <MaskedReveal>
            <Heading
              as="h3"
              position={{ lg: "sticky" }}
              top={{ lg: "120px" }}
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight={{ base: 1.3, md: 1.16 }}
              letterSpacing="-0.04em"
              maxW={{ base: "100%", md: "21ch" }}
              sx={{ textWrap: "balance" }}
            >
              {t("v2Home.why.statement")}
            </Heading>
          </MaskedReveal>

          <Box>
            <MaskedReveal>
              <Flex
                data-section-number="03"
                justify={{ base: "flex-start", lg: "flex-end" }}
                align="center"
                gap={3}
                color="gray.500"
                letterSpacing="0.12em"
                textTransform="uppercase"
                mb={{ base: 7, md: 10 }}
              >
                <Text color="#25272D" fontWeight="700">
                  03
                </Text>
                <Heading
                  as="h2"
                  fontSize={{ base: "sm", md: "md" }}
                  letterSpacing="0.12em"
                >
                  {t("v2Home.why.title")}
                </Heading>
              </Flex>
            </MaskedReveal>

            {showDesktopCapabilities ? (
              <Grid
                data-testid="product-capabilities-desktop"
                templateColumns="repeat(2, minmax(0, 1fr))"
                gap={{ md: 5 }}
                alignItems="start"
              >
                <VStack
                  data-testid="product-capabilities-left"
                  spacing={{ base: 4, md: 5 }}
                  align="stretch"
                >
                  {productItems
                    .filter((_, index) => index % 2 === 0)
                    .map((item, index) => renderProductCard(item, index * 2))}
                </VStack>
                <VStack
                  data-testid="product-capabilities-right"
                  data-offset="140px"
                  spacing={{ base: 4, md: 5 }}
                  align="stretch"
                  pt="140px"
                >
                  {productItems
                    .filter((_, index) => index % 2 === 1)
                    .map((item, index) =>
                      renderProductCard(item, index * 2 + 1),
                    )}
                </VStack>
              </Grid>
            ) : (
              <VStack
                data-testid="product-capabilities-mobile"
                spacing={4}
                align="stretch"
              >
                {productItems.map(renderProductCard)}
              </VStack>
            )}
          </Box>
        </Grid>
      </SectionShell>

      <Modal
        isOpen={Boolean(selectedProduct)}
        onClose={closeProductModal}
        isCentered
        size="4xl"
        scrollBehavior="inside"
        motionPreset="scale"
        returnFocusOnClose
      >
        <ModalOverlay
          data-testid="product-modal-overlay"
          bg="rgba(12, 15, 18, 0.84)"
          backdropFilter="blur(5px)"
          onClick={closeProductModal}
        />
        <ModalContent
          id="product-capability-dialog"
          data-testid="product-capability-dialog"
          aria-labelledby="product-capability-dialog-title"
          aria-describedby={
            selectedProduct && selectedProductHasDescription
              ? "product-capability-dialog-description"
              : undefined
          }
          maxW={{
            base: "calc(100vw - 24px)",
            md: "1040px",
          }}
          minH={{ base: "auto", md: "520px" }}
          maxH={{
            base: "calc(100vh - 24px)",
            md: "min(800px, calc(100vh - 48px))",
          }}
          m={{ base: 3, md: 6 }}
          borderRadius={{ base: "2xl", md: "3xl" }}
          overflow="hidden"
          boxShadow="0 30px 90px rgba(0,0,0,.32)"
        >
          <ModalCloseButton
            data-testid="product-modal-close"
            aria-label={t("v2Home.why.modal.closeLabel")}
            top={{ base: 4, md: 6 }}
            right={{ base: 4, md: 6 }}
            size="lg"
            color="gray.500"
            zIndex={2}
          />
          {selectedProduct && selectedProductImage && (
            <ModalBody p={{ base: 6, md: 12 }} pt={{ base: 16, md: 14 }}>
              <Grid
                data-testid={`product-detail-gallery-${selectedProduct.key}`}
                templateColumns={{ base: "1fr", md: "0.72fr 1.28fr" }}
                gap={{ base: 7, md: 12 }}
                alignItems="center"
              >
                <Box>
                  <Heading
                    id="product-capability-dialog-title"
                    as="h3"
                    fontSize={{ base: "2xl", md: "4xl" }}
                    lineHeight="1.2"
                    mb={{ base: 4, md: 5 }}
                  >
                    {t(`v2Home.why.items.${selectedProduct.key}.title`)}
                  </Heading>
                  {selectedProductHasDescription && (
                    <Text
                      id="product-capability-dialog-description"
                      data-testid="product-modal-description"
                      color="gray.700"
                      fontSize={{ base: "md", md: "lg" }}
                      lineHeight={{ base: 1.8, md: 1.9 }}
                      mb={{ base: 5, md: 7 }}
                    >
                      {t(`v2Home.why.items.${selectedProduct.key}.description`)}
                    </Text>
                  )}
                  {selectedProductImages.length > 1 && (
                    <Flex align="center" gap={3}>
                      <Box
                        as="button"
                        type="button"
                        data-testid="product-gallery-previous"
                        aria-label={t("v2Home.why.modal.previousLabel")}
                        onClick={showPreviousProductImage}
                        w="44px"
                        h="44px"
                        borderRadius="full"
                        border="1px solid"
                        borderColor="blackAlpha.300"
                        bg="white"
                        fontSize="xl"
                        lineHeight="1"
                        transition="border-color 160ms ease, color 160ms ease"
                        _hover={{
                          borderColor: "primary.400",
                          color: "primary.500",
                        }}
                        _focusVisible={{
                          outline: "3px solid",
                          outlineColor: "primary.400",
                          outlineOffset: "3px",
                        }}
                      >
                        ←
                      </Box>
                      <Text
                        data-testid="product-gallery-progress"
                        minW="48px"
                        textAlign="center"
                        fontWeight="700"
                        color="gray.600"
                      >
                        {selectedProductImageIndex + 1} /{" "}
                        {selectedProductImages.length}
                      </Text>
                      <Box
                        as="button"
                        type="button"
                        data-testid="product-gallery-next"
                        aria-label={t("v2Home.why.modal.nextLabel")}
                        onClick={showNextProductImage}
                        w="44px"
                        h="44px"
                        borderRadius="full"
                        border="1px solid"
                        borderColor="blackAlpha.300"
                        bg="white"
                        fontSize="xl"
                        lineHeight="1"
                        transition="border-color 160ms ease, color 160ms ease"
                        _hover={{
                          borderColor: "primary.400",
                          color: "primary.500",
                        }}
                        _focusVisible={{
                          outline: "3px solid",
                          outlineColor: "primary.400",
                          outlineOffset: "3px",
                        }}
                      >
                        →
                      </Box>
                    </Flex>
                  )}
                </Box>

                <Box minW={0}>
                  <Box
                    borderRadius="2xl"
                    overflow="hidden"
                    bg="linear-gradient(145deg, #fff8ef 0%, #f6f2ff 52%, #eef8ff 100%)"
                    border="1px solid"
                    borderColor="blackAlpha.200"
                    p={{ base: 3, md: 4 }}
                  >
                    <Image
                      data-testid={`product-detail-image-${selectedProduct.key}-${
                        selectedProductImageIndex + 1
                      }`}
                      src={selectedProductImage}
                      alt={t(
                        `v2Home.why.items.${selectedProduct.key}.galleryImageAlt${
                          selectedProductImageIndex + 1
                        }`,
                      )}
                      w="100%"
                      h={{ base: "58vh", md: "570px" }}
                      maxH={{ base: "600px", md: "64vh" }}
                      objectFit="contain"
                      objectPosition="center"
                    />
                  </Box>

                  {selectedProductImages.length > 1 && (
                    <Flex
                      mt={4}
                      gap={3}
                      overflowX="auto"
                      pb={1}
                      justify={{ md: "center" }}
                      aria-label={t("v2Home.why.modal.thumbnailLabel")}
                    >
                      {selectedProductImages.map((image, index) => {
                        const isSelected = index === selectedProductImageIndex;

                        return (
                          <Box
                            as="button"
                            type="button"
                            key={image}
                            data-testid={`product-gallery-thumbnail-${index + 1}`}
                            aria-label={t("v2Home.why.modal.showImageLabel", {
                              index: index + 1,
                            })}
                            aria-pressed={isSelected}
                            onClick={() => setSelectedProductImageIndex(index)}
                            flex="0 0 auto"
                            w={{ base: "58px", md: "68px" }}
                            h={{ base: "86px", md: "102px" }}
                            p={1}
                            borderRadius="lg"
                            overflow="hidden"
                            border="2px solid"
                            borderColor={
                              isSelected ? "primary.400" : "blackAlpha.200"
                            }
                            bg="white"
                            opacity={isSelected ? 1 : 0.68}
                            transition="opacity 160ms ease, border-color 160ms ease"
                            _hover={{ opacity: 1 }}
                            _focusVisible={{
                              outline: "3px solid",
                              outlineColor: "primary.400",
                              outlineOffset: "2px",
                            }}
                          >
                            <Image
                              src={image}
                              alt=""
                              w="100%"
                              h="100%"
                              objectFit="contain"
                            />
                          </Box>
                        );
                      })}
                    </Flex>
                  )}
                </Box>
              </Grid>
            </ModalBody>
          )}
          {selectedProduct && !selectedProductImage && (
            <ModalBody
              data-testid="product-empty-modal"
              p={{ base: 6, md: 12 }}
              pt={{ base: 16, md: 14 }}
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <Heading
                id="product-capability-dialog-title"
                as="h3"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.2"
                mb={selectedProductHasDescription ? 5 : 0}
              >
                {t(`v2Home.why.items.${selectedProduct.key}.title`)}
              </Heading>
              {selectedProductHasDescription && (
                <Text
                  id="product-capability-dialog-description"
                  data-testid="product-modal-description"
                  color="gray.700"
                  fontSize={{ base: "md", md: "lg" }}
                  lineHeight={{ base: 1.8, md: 1.9 }}
                  maxW="62ch"
                >
                  {t(`v2Home.why.items.${selectedProduct.key}.description`)}
                </Text>
              )}
            </ModalBody>
          )}
        </ModalContent>
      </Modal>

      <SectionShell id="who-deep-soul" tinted>
        <MaskedReveal>
          <SectionHeading
            number="04"
            title={t("v2Home.audience.title")}
            statement={t("v2Home.audience.statement")}
          />
        </MaskedReveal>

        <Box
          data-testid="audience-layout-desktop"
          display={{ base: "none", lg: "block" }}
          position="relative"
          minH="620px"
          maxW="1200px"
          mx="auto"
          overflow="hidden"
        >
          <Box
            as="svg"
            data-testid="audience-orbit"
            aria-label={t("v2Home.audience.title")}
            role="img"
            position="absolute"
            inset="0"
            w="100%"
            h="100%"
            viewBox="0 0 1200 620"
            preserveAspectRatio="none"
            pointerEvents="none"
          >
            <defs>
              <linearGradient
                id="audience-orbit-gradient"
                x1="0"
                y1="0"
                x2="1"
                y2="1"
              >
                <stop offset="0%" stopColor="rgba(255, 111, 128, .34)" />
                <stop offset="100%" stopColor="rgba(132, 177, 255, .3)" />
              </linearGradient>
              <filter
                id="audience-active-glow"
                filterUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="1200"
                height="620"
              >
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <ellipse
              cx="600"
              cy="310"
              rx="430"
              ry="260"
              fill="none"
              stroke="url(#audience-orbit-gradient)"
              strokeWidth="2"
            />
            {audienceItems.map(({ key }) => (
              <path
                key={key}
                data-testid={`audience-connector-${key}`}
                data-active={selectedAudienceKey === key ? "true" : "false"}
                d={audienceConnectorPaths[key]}
                fill="none"
                stroke={
                  selectedAudienceKey === key
                    ? "#FF3F4F"
                    : "rgba(94, 112, 150, .2)"
                }
                strokeWidth={selectedAudienceKey === key ? 3 : 1.5}
                filter={
                  selectedAudienceKey === key
                    ? "url(#audience-active-glow)"
                    : undefined
                }
                style={{
                  transition: prefersReducedMotion
                    ? "none"
                    : "stroke 180ms ease, stroke-width 180ms ease, filter 180ms ease",
                }}
              />
            ))}
          </Box>

          {audienceItems.map((item) => (
            <Box
              key={item.key}
              position="absolute"
              top={item.orbitPosition.top}
              {...(item.orbitPosition.side === "left"
                ? { left: 0 }
                : { right: 0 })}
              zIndex={2}
            >
              {renderAudienceButton(item, "orbit")}
            </Box>
          ))}

          <Box
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            w="min(42%, 440px)"
            minH="390px"
            zIndex={1}
          >
            {renderAudienceDetail()}
          </Box>
        </Box>

        <Box
          data-testid="audience-layout-tablet"
          display={{ base: "none", md: "block", lg: "none" }}
        >
          <SimpleGrid columns={2} spacing={5} mb={7}>
            {audienceItems.map((item) => renderAudienceButton(item, "grid"))}
          </SimpleGrid>
          {renderAudienceDetail(true)}
        </Box>

        <Box
          data-testid="audience-layout-mobile"
          display={{ base: "block", md: "none" }}
          overflow="hidden"
        >
          <Box
            data-testid="audience-mobile-orbit"
            position="relative"
            w="100%"
            maxW="430px"
            h={{ base: "560px", sm: "590px" }}
            mx="auto"
            overflow="hidden"
          >
            <Box
              as="svg"
              data-testid="audience-mobile-orbit-svg"
              aria-label={t("v2Home.audience.title")}
              role="img"
              position="absolute"
              inset="0"
              w="100%"
              h="100%"
              viewBox="0 0 360 540"
              preserveAspectRatio="none"
              pointerEvents="none"
            >
              <defs>
                <linearGradient
                  id="audience-mobile-orbit-gradient"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop offset="0%" stopColor="rgba(255,111,128,.4)" />
                  <stop offset="100%" stopColor="rgba(132,177,255,.36)" />
                </linearGradient>
                <filter
                  id="audience-mobile-active-glow"
                  filterUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="360"
                  height="540"
                >
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <ellipse
                cx="180"
                cy="270"
                rx="132"
                ry="236"
                fill="none"
                stroke="url(#audience-mobile-orbit-gradient)"
                strokeWidth="1.8"
              />
              {audienceItems.map(({ key }) => (
                <path
                  key={key}
                  data-testid={`audience-mobile-connector-${key}`}
                  data-active={selectedAudienceKey === key ? "true" : "false"}
                  d={mobileAudienceConnectorPaths[key]}
                  fill="none"
                  stroke={
                    selectedAudienceKey === key
                      ? "#FF3F4F"
                      : "rgba(94,112,150,.2)"
                  }
                  strokeWidth={selectedAudienceKey === key ? 2.6 : 1.4}
                  filter={
                    selectedAudienceKey === key
                      ? "url(#audience-mobile-active-glow)"
                      : undefined
                  }
                  style={{
                    transition: prefersReducedMotion
                      ? "none"
                      : "stroke 180ms ease, stroke-width 180ms ease, filter 180ms ease",
                  }}
                />
              ))}
            </Box>

            {audienceItems.map((item) => (
              <Box
                key={item.key}
                position="absolute"
                top={item.mobilePosition.top}
                {...(item.mobilePosition.side === "left"
                  ? { left: 0 }
                  : { right: 0 })}
                zIndex={2}
              >
                {renderMobileAudienceNode(item)}
              </Box>
            ))}

            {renderMobileAudienceDetail()}
          </Box>
          <Text
            data-testid="audience-mobile-conclusion-below"
            display="none"
            mt={2}
            px={5}
            color="gray.800"
            fontSize="sm"
            fontWeight="700"
            textAlign="center"
            lineHeight="1.65"
            sx={{ "@media (max-width: 360px)": { display: "block" } }}
          >
            {t("v2Home.audience.conclusion")}
          </Text>
        </Box>
      </SectionShell>

      <SectionShell id="why-trust-us">
        <MaskedReveal>
          <SectionHeading
            number="05"
            title={t("v2Home.trust.title")}
            statement={t("v2Home.trust.statement")}
            reversed
          />
        </MaskedReveal>
        <Grid templateColumns={{ base: "1fr", lg: "0.8fr 1.2fr" }} gap={8}>
          <Box
            minH={{ lg: "100%" }}
            borderRadius="3xl"
            overflow="hidden"
            border="1px solid"
            borderColor="blackAlpha.200"
            bg="white"
            display="flex"
            flexDirection="column"
            data-testid="trust-visual"
          >
            <Box
              position="relative"
              aspectRatio={{ base: "16 / 9", lg: "auto" }}
              minH={{ lg: "300px" }}
              flex={{ lg: "1 1 auto" }}
              overflow="hidden"
              data-testid="trust-visual-image-frame"
            >
              <Image
                src={securityLayersImage}
                alt={t("v2Home.trust.imageAlt")}
                position="absolute"
                inset={0}
                w="100%"
                h="100%"
                objectFit="contain"
                objectPosition="center"
                data-testid="trust-visual-image"
              />
            </Box>
            <SimpleGrid
              columns={{ base: 2, sm: 4 }}
              spacing={2}
              px={{ base: 4, md: 6 }}
              pt={{ base: 4, md: 5 }}
            >
              {(
                ["identity", "transmission", "storage", "aiProcessing"] as const
              ).map((layer, index) => (
                <Box key={layer} textAlign="center">
                  <Text color="primary.500" fontSize="xs" fontWeight="800">
                    0{index + 1}
                  </Text>
                  <Text color="gray.700" fontSize="xs" fontWeight="700">
                    {t(`v2Home.trust.layers.${layer}`)}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
            <Box p={{ base: 6, md: 8 }}>
              <Text
                color="gray.800"
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="700"
                lineHeight="1.45"
                data-testid="trust-visual-caption"
              >
                {t("v2Home.trust.visualCaption")}
              </Text>
            </Box>
          </Box>
          <Accordion
            allowToggle
            display="grid"
            gridTemplateColumns={{
              base: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
            }}
            gap={5}
            alignItems="start"
          >
            {trustItems.map((key, index) => (
              <AccordionItem
                key={key}
                border="1px solid"
                borderColor="blackAlpha.200"
                borderRadius="2xl"
                overflow="hidden"
                bg="white"
              >
                <h3>
                  <AccordionButton
                    p={{ base: 6, md: 7 }}
                    alignItems="flex-start"
                    _hover={{ bg: "blackAlpha.50" }}
                    _expanded={{ bg: "rgba(255, 63, 79, 0.04)" }}
                    _focusVisible={{
                      outline: "3px solid rgba(255, 63, 79, 0.38)",
                      outlineOffset: "-3px",
                    }}
                  >
                    <Box flex="1" textAlign="left">
                      <Text
                        color="primary.500"
                        fontSize="sm"
                        fontWeight="700"
                        mb={2}
                      >
                        0{index + 1}
                      </Text>
                      <Heading as="span" display="block" fontSize="lg" mb={2}>
                        {t(`v2Home.trust.items.${key}.title`)}
                      </Heading>
                      <Text color="gray.600" fontSize="sm" lineHeight="1.7">
                        {getTrustSummary(key)}
                      </Text>
                    </Box>
                    <AccordionIcon ml={4} />
                  </AccordionButton>
                </h3>
                <AccordionPanel
                  px={{ base: 6, md: 7 }}
                  pt={0}
                  pb={{ base: 6, md: 7 }}
                  color="gray.700"
                  fontSize="sm"
                  lineHeight="1.8"
                  borderTop="1px solid"
                  borderColor="blackAlpha.100"
                >
                  {t(`v2Home.trust.items.${key}.description`)}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Grid>
        <MaskedReveal>
          <Grid
            data-testid="trust-assessment-badge"
            mt={{ base: 8, md: 10 }}
            templateColumns={{
              base: "1fr",
              md: "minmax(240px, 0.68fr) minmax(0, 1.32fr)",
            }}
            borderRadius="3xl"
            overflow="hidden"
            border="1px solid"
            borderColor="blackAlpha.200"
            bg="white"
            boxShadow="0 18px 50px rgba(72,84,96,.08)"
          >
            <Flex
              minH={{ base: "210px", md: "240px" }}
              align="center"
              justify="center"
              bg="linear-gradient(145deg, #fff8ef 0%, #f6f2ff 52%, #eef8ff 100%)"
              p={{ base: 5, md: 6 }}
            >
              <Image
                data-testid="trust-assessment-badge-image"
                src={securityAssessmentBadgeImage}
                alt={t("v2Home.trust.assessmentBadge.imageAlt")}
                w="100%"
                h="auto"
                objectFit="contain"
                objectPosition="center"
                display="block"
              />
            </Flex>
            <Flex p={{ base: 7, md: 10 }} direction="column" justify="center">
              <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} mb={4}>
                {t("v2Home.trust.assessmentBadge.title")}
              </Heading>
              <Text
                data-testid="trust-assessment-badge-description"
                color="gray.700"
                lineHeight="1.85"
                maxW="70ch"
              >
                {t("v2Home.trust.assessmentBadge.description")}
              </Text>
            </Flex>
          </Grid>
        </MaskedReveal>
      </SectionShell>

      <SectionShell id="patent-innovation" tinted>
        <MaskedReveal>
          <SectionHeading
            number="06"
            title={t("v2Home.innovation.title")}
            statement={t("v2Home.innovation.statement")}
          />
        </MaskedReveal>
        <Grid
          templateColumns={{
            base: "1fr",
            lg: "minmax(0, 3fr) minmax(320px, 2fr)",
          }}
          gap={{ base: 10, lg: 14 }}
          alignItems="center"
        >
          <Box
            p={{ base: 1.5, md: 2 }}
            bg="whiteAlpha.900"
            border="1px solid"
            borderColor="blackAlpha.100"
            borderRadius={{ base: "16px", md: "22px" }}
            boxShadow="0 14px 36px rgba(15, 23, 42, 0.10)"
            overflow="hidden"
          >
            <Image
              src={patentInnovationBanner}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              display="block"
              w="100%"
              h="auto"
              borderRadius={{ base: "12px", md: "16px" }}
            />
          </Box>

          <Box
            minW={0}
            pl={{ base: 0, lg: 8 }}
            borderLeftWidth={{ base: 0, lg: "2px" }}
            borderLeftStyle="solid"
            borderLeftColor={{ base: "transparent", lg: "primary.300" }}
          >
            <Box data-testid="innovation-metric" w="100%">
              <Flex align="baseline" gap={{ base: 4, md: 5 }}>
                <Text
                  as="span"
                  color="primary.500"
                  fontSize={{ base: "6xl", md: "8xl" }}
                  fontWeight="800"
                  lineHeight=".82"
                  letterSpacing="-0.08em"
                >
                  2
                </Text>
                <Text
                  maxW="11em"
                  color="gray.700"
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="700"
                  lineHeight="1.4"
                >
                  {t("v2Home.innovation.proof.metricLabel")}
                </Text>
              </Flex>

              <Box h="1px" bg="blackAlpha.300" my={{ base: 6, md: 8 }} />

              <VStack align="stretch" spacing={4}>
                {["chinaPatent", "hongKongPatent"].map((key, index) => (
                  <Flex key={key} align="baseline" gap={4}>
                    <Text
                      minW="2em"
                      color="gray.400"
                      fontSize="xs"
                      fontWeight="800"
                      letterSpacing=".08em"
                    >
                      0{index + 1}
                    </Text>
                    <Text color="gray.800" fontSize="sm" fontWeight="700">
                      {t(`v2Home.innovation.proof.${key}`)}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Box>

            <Box w="100%" mt={{ base: 8, md: 10 }}>
              <Accordion allowMultiple>
                {["framework", "progress"].map((key, index) => (
                  <AccordionItem
                    key={key}
                    borderColor="blackAlpha.300"
                    _last={{ borderBottomWidth: "1px" }}
                  >
                    <h4>
                      <AccordionButton
                        data-testid={`innovation-accordion-button-${key}`}
                        data-transition-disabled={
                          prefersReducedMotion ? "true" : "false"
                        }
                        px={0}
                        py={{ base: 6, md: 8 }}
                        gap={{ base: 4, md: 6 }}
                        color="gray.900"
                        transition={
                          prefersReducedMotion ? "none" : "color 180ms ease"
                        }
                        _hover={{ color: "primary.600" }}
                        _expanded={{ color: "primary.600" }}
                        _focusVisible={{
                          outline: "none",
                          boxShadow:
                            "inset 0 -3px 0 var(--chakra-colors-primary-300)",
                        }}
                        sx={{
                          "&[aria-expanded=true] .innovation-toggle::after": {
                            transform: "translate(-50%, -50%) scaleY(0)",
                          },
                        }}
                      >
                        <Text
                          minW={{ base: "2em", md: "2.4em" }}
                          color="primary.500"
                          fontSize="sm"
                          fontWeight="800"
                          letterSpacing=".08em"
                          textAlign="left"
                        >
                          0{index + 1}
                        </Text>
                        <Box flex="1" textAlign="left">
                          <Heading
                            as="span"
                            fontSize={{ base: "lg", md: "2xl" }}
                            lineHeight="1.3"
                          >
                            {t(`v2Home.innovation.items.${key}.title`)}
                          </Heading>
                        </Box>
                        <Box
                          className="innovation-toggle"
                          data-testid={`innovation-toggle-${key}`}
                          aria-hidden="true"
                          position="relative"
                          flex="0 0 auto"
                          w="28px"
                          h="28px"
                          _before={{
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            w: "14px",
                            h: "1.5px",
                            bg: "currentColor",
                            transform: "translate(-50%, -50%)",
                          }}
                          _after={{
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            w: "1.5px",
                            h: "14px",
                            bg: "currentColor",
                            transform: "translate(-50%, -50%) scaleY(1)",
                            transformOrigin: "center",
                            transition: prefersReducedMotion
                              ? "none"
                              : "transform 180ms ease",
                          }}
                        />
                      </AccordionButton>
                    </h4>
                    <AccordionPanel
                      pl={{ base: 0, md: "4.8em" }}
                      pr={{ base: 0, md: 12 }}
                      pb={8}
                      color="gray.600"
                      lineHeight="1.9"
                    >
                      <Text maxW="62ch">
                        {t(`v2Home.innovation.items.${key}.description`)}
                      </Text>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </Box>
          </Box>
        </Grid>
      </SectionShell>

      <SectionShell id="industry-recognition">
        <MaskedReveal>
          <SectionHeading
            number="07"
            title={t("v2Home.recognition.title")}
            statement={t("v2Home.recognition.statement")}
            reversed
          />
        </MaskedReveal>
        <VStack spacing={{ base: 5, md: 7 }} align="stretch">
          {recognitionItems.map(({ key, image, objectFit }, index) => (
            <MaskedReveal key={key} delay={index * 0.08}>
              <Grid
                data-testid={`recognition-card-${key}`}
                templateColumns={{
                  base: "1fr",
                  md: "minmax(240px, 0.68fr) minmax(0, 1.32fr)",
                }}
                borderRadius="3xl"
                overflow="hidden"
                border="1px solid"
                borderColor="blackAlpha.200"
                bg="white"
                boxShadow="0 18px 50px rgba(72,84,96,.08)"
              >
                <Box
                  minH={{ base: "210px", md: "240px" }}
                  bg="linear-gradient(145deg, #fff8ef 0%, #f6f2ff 52%, #eef8ff 100%)"
                  p={objectFit === "contain" ? { base: 7, md: 9 } : 0}
                >
                  <Image
                    data-testid={`recognition-image-${key}`}
                    src={image}
                    alt={t(`v2Home.recognition.items.${key}.imageAlt`)}
                    w="100%"
                    h="100%"
                    minH={{ base: "210px", md: "240px" }}
                    objectFit={objectFit}
                    objectPosition="center"
                  />
                </Box>
                <Flex
                  p={{ base: 7, md: 10 }}
                  direction="column"
                  justify="center"
                >
                  <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} mb={2}>
                    {t(`v2Home.recognition.items.${key}.title`)}
                  </Heading>
                  <Text color="primary.600" fontWeight="700" mb={4}>
                    {t(`v2Home.recognition.items.${key}.subtitle`)}
                  </Text>
                  <Text color="gray.700" lineHeight="1.85" maxW="70ch">
                    {t(`v2Home.recognition.items.${key}.description`)}
                  </Text>
                </Flex>
              </Grid>
            </MaskedReveal>
          ))}
        </VStack>
      </SectionShell>

      <SectionShell id="partnership">
        <MaskedReveal>
          <SectionHeading
            number="08"
            title={t("v2Home.partnership.title")}
            statement={t("v2Home.partnership.statement")}
            reversed
          />
        </MaskedReveal>
        <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} mb={6}>
          {t("v2Home.partnership.partnersLabel")}
        </Heading>
        <Box
          data-testid="partner-logo-carousel"
          data-visual-style="frameless-stage"
          maxW="900px"
          mx="auto"
          mb={{ base: 12, md: 16 }}
          sx={{
            ".partner-logo-swiper": {
              paddingBottom: { base: "30px", md: "34px" },
              perspective: "1200px",
            },
            ".partner-logo-swiper .swiper-wrapper": {
              alignItems: "stretch",
            },
            ".partner-logo-swiper .swiper-slide": {
              height: "auto",
              opacity: 0.42,
              filter: "saturate(0.72)",
              transition: prefersReducedMotion
                ? "none"
                : "opacity 900ms ease, filter 900ms ease",
            },
            ".partner-logo-swiper .swiper-slide-active": {
              opacity: 1,
              filter: "saturate(1)",
            },
            ".partner-logo-swiper .partner-logo-reveal": {
              height: "100%",
            },
            ".partner-logo-swiper .partner-logo-panel": {
              position: "relative",
              isolation: "isolate",
              transition: prefersReducedMotion
                ? "none"
                : "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
            },
            ".partner-logo-swiper .partner-logo-panel::before": {
              content: '""',
              position: "absolute",
              top: "42%",
              left: "50%",
              zIndex: 0,
              width: "120%",
              height: "80%",
              pointerEvents: "none",
              background:
                "radial-gradient(ellipse at center, rgba(255,108,129,.2) 0%, rgba(208,228,255,.16) 42%, rgba(255,255,255,0) 72%)",
              filter: "blur(12px)",
              opacity: 0,
              transform: "translate(-50%, -50%) scale(.86)",
              transition: prefersReducedMotion
                ? "none"
                : "opacity 900ms ease, transform 900ms ease",
            },
            ".partner-logo-swiper .partner-logo-panel > *": {
              position: "relative",
              zIndex: 1,
            },
            ".partner-logo-swiper .swiper-slide-active .partner-logo-panel": {
              transform: prefersReducedMotion ? "none" : "translateY(-2px)",
            },
            ".partner-logo-swiper .swiper-slide-active .partner-logo-panel::before":
              {
                opacity: 1,
                transform: "translate(-50%, -50%) scale(1)",
              },
            ".partner-logo-swiper .partner-logo-name": {
              opacity: { base: 0, md: 0.25 },
              transition: prefersReducedMotion ? "none" : "opacity 900ms ease",
            },
            ".partner-logo-swiper .swiper-slide-active .partner-logo-name": {
              opacity: 1,
            },
            ".partner-logo-swiper .swiper-pagination": {
              bottom: "4px",
            },
            ".partner-logo-swiper .swiper-pagination-bullet": {
              background: "#8d8992",
              opacity: 0.3,
            },
            ".partner-logo-swiper .swiper-pagination-bullet-active": {
              background: "#ff4055",
              opacity: 1,
            },
          }}
        >
          <Swiper
            effect={prefersReducedMotion ? "slide" : "coverflow"}
            centeredSlides={!prefersReducedMotion}
            loop={!prefersReducedMotion}
            grabCursor={!prefersReducedMotion}
            slidesPerView={prefersReducedMotion ? 1 : 1.9}
            slidesPerGroup={1}
            breakpoints={
              prefersReducedMotion
                ? undefined
                : {
                    768: { slidesPerView: 1.75, slidesPerGroup: 1 },
                    992: { slidesPerView: 2.25, slidesPerGroup: 1 },
                  }
            }
            coverflowEffect={{
              rotate: 28,
              stretch: 0,
              depth: 150,
              modifier: 1,
              scale: 0.8,
              slideShadows: false,
            }}
            speed={prefersReducedMotion ? 0 : 900}
            pagination={{ clickable: true, dynamicBullets: true }}
            autoplay={
              prefersReducedMotion
                ? false
                : {
                    delay: 3800,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                  }
            }
            watchOverflow
            modules={[Autoplay, EffectCoverflow, Pagination]}
            className="partner-logo-swiper"
          >
            {partnerItems.map(({ key, image, logoScale }, index) => (
              <SwiperSlide key={key} data-testid={`partner-slide-${key}`}>
                <MaskedReveal
                  delay={index * 0.08}
                  className="partner-logo-reveal"
                >
                  <Box
                    className="partner-logo-panel"
                    data-testid={`partner-card-${key}`}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={3}
                    h="100%"
                    minH={{ base: "190px", md: "220px" }}
                    mx={{ base: 2, md: 3 }}
                    p={{ base: 4, md: 5 }}
                    overflow="visible"
                    textAlign="center"
                  >
                    <Box
                      h={{ base: "84px", md: "104px" }}
                      w="100%"
                      overflow="hidden"
                    >
                      <Image
                        data-testid={`partner-logo-${key}`}
                        src={image}
                        alt={t(`v2Home.partnership.items.${key}.imageAlt`)}
                        w="100%"
                        h="100%"
                        objectFit="contain"
                        transform={`scale(${logoScale})`}
                        transformOrigin="center"
                      />
                    </Box>
                    <Heading
                      className="partner-logo-name"
                      as="h4"
                      fontSize={{ base: "md", md: "lg" }}
                      lineHeight="1.4"
                      textAlign="center"
                    >
                      {t(`v2Home.partnership.items.${key}.title`)}
                    </Heading>
                  </Box>
                </MaskedReveal>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
        <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} mb={6}>
          {t("v2Home.partnership.casesLabel")}
        </Heading>
        <SimpleGrid
          data-testid="partnership-cases"
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={6}
          mb={{ base: 12, md: 16 }}
        >
          {partnershipCases.map(({ key, image }, index) => (
            <MaskedReveal key={key} delay={index * 0.08}>
              <Button
                data-testid={`partnership-case-${key}`}
                aria-label={t(`v2Home.partnership.cases.${key}.title`)}
                aria-haspopup="dialog"
                aria-controls="partnership-case-dialog"
                onClick={() => setSelectedPartnershipCaseIndex(index)}
                display="flex"
                flexDirection="column"
                alignItems="stretch"
                justifyContent="flex-start"
                w="100%"
                h="100%"
                minH={0}
                p={0}
                overflow="hidden"
                whiteSpace="normal"
                textAlign="left"
                bg="white"
                border="1px solid"
                borderColor="blackAlpha.200"
                borderRadius="3xl"
                transition="transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease"
                _hover={{
                  bg: "white",
                  borderColor: "primary.300",
                  boxShadow: "0 18px 42px rgba(44, 54, 72, 0.12)",
                  transform: "translateY(-3px)",
                }}
                _focusVisible={{
                  outline: "3px solid",
                  outlineColor: "primary.400",
                  outlineOffset: "4px",
                }}
              >
                <Box
                  data-testid={`partnership-case-image-frame-${key}`}
                  w="100%"
                  aspectRatio="1122 / 1402"
                  bg="gray.50"
                  overflow="hidden"
                >
                  <Image
                    data-testid={`partnership-case-image-${key}`}
                    src={image}
                    alt={t(`v2Home.partnership.cases.${key}.imageAlt`)}
                    w="100%"
                    h="100%"
                    objectFit="contain"
                  />
                </Box>
                <Box p={{ base: 5, md: 6 }} w="100%">
                  <Heading as="h4" fontSize={{ base: "lg", md: "xl" }} mb={2}>
                    {t(`v2Home.partnership.cases.${key}.title`)}
                  </Heading>
                  <Text color="gray.600" fontWeight="600">
                    {t(`v2Home.partnership.cases.${key}.partner`)}
                  </Text>
                </Box>
              </Button>
            </MaskedReveal>
          ))}
        </SimpleGrid>
        <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} mb={6}>
          {t("v2Home.partnership.contextsLabel")}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {partnershipContexts.map(({ key, image, fullBleed }, index) => (
            <MaskedReveal key={key} delay={index * 0.08}>
              <Grid
                data-testid={`partner-card-${key}`}
                h="100%"
                templateColumns={{ base: "1fr", sm: "180px 1fr" }}
                borderRadius="3xl"
                overflow="hidden"
                border="1px solid"
                borderColor="blackAlpha.200"
              >
                <Box
                  h={{ base: "190px", sm: "100%" }}
                  minH={{ sm: "180px" }}
                  bg="gray.50"
                  p={fullBleed ? 0 : 6}
                >
                  <Image
                    src={image}
                    alt={t(`v2Home.partnership.items.${key}.imageAlt`)}
                    w="100%"
                    h="100%"
                    objectFit={fullBleed ? "cover" : "contain"}
                  />
                </Box>
                <Flex
                  p={{ base: 6, md: 7 }}
                  direction="column"
                  justify="center"
                >
                  <Heading as="h4" fontSize="xl" mb={3}>
                    {t(`v2Home.partnership.items.${key}.title`)}
                  </Heading>
                  <Text color="gray.600" lineHeight="1.8">
                    {t(`v2Home.partnership.items.${key}.description`)}
                  </Text>
                </Flex>
              </Grid>
            </MaskedReveal>
          ))}
        </SimpleGrid>

        <Modal
          isOpen={selectedPartnershipCaseIndex !== null}
          onClose={closePartnershipCaseModal}
          size="6xl"
          isCentered
          scrollBehavior="inside"
        >
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent
            id="partnership-case-dialog"
            data-testid="partnership-case-lightbox"
            maxW={{
              base: "calc(100vw - 24px)",
              md: "min(1100px, calc(100vw - 64px))",
            }}
            maxH="94vh"
            m={{ base: 3, md: 8 }}
            overflow="hidden"
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                showPreviousPartnershipCase();
              }
              if (event.key === "ArrowRight") {
                event.preventDefault();
                showNextPartnershipCase();
              }
            }}
          >
            <ModalCloseButton
              aria-label={t("v2Home.partnership.caseClose")}
              zIndex={2}
              bg="whiteAlpha.900"
            />
            <ModalBody p={{ base: 3, md: 6 }} overflowX="hidden">
              <Flex direction="column" align="center" minW={0}>
                <Image
                  data-testid="partnership-case-lightbox-image"
                  src={selectedPartnershipCase.image}
                  alt={t(
                    `v2Home.partnership.cases.${selectedPartnershipCase.key}.imageAlt`,
                  )}
                  w="100%"
                  maxH={{ base: "68vh", md: "70vh" }}
                  objectFit="contain"
                />
                <Box w="100%" pt={{ base: 4, md: 5 }} px={{ base: 1, md: 2 }}>
                  <Heading as="h3" fontSize={{ base: "lg", md: "2xl" }} pr={10}>
                    {t(
                      `v2Home.partnership.cases.${selectedPartnershipCase.key}.title`,
                    )}
                  </Heading>
                  <Text color="gray.600" mt={1}>
                    {t(
                      `v2Home.partnership.cases.${selectedPartnershipCase.key}.partner`,
                    )}
                  </Text>
                  <Flex align="center" justify="space-between" gap={3} mt={5}>
                    <Button
                      data-testid="partnership-case-previous"
                      aria-label={t("v2Home.partnership.casePrevious")}
                      onClick={showPreviousPartnershipCase}
                      minW={{ base: 12, sm: 32 }}
                      minH={12}
                    >
                      <Text aria-hidden="true" mr={{ base: 0, sm: 2 }}>
                        ←
                      </Text>
                      <Text display={{ base: "none", sm: "block" }}>
                        {t("v2Home.partnership.casePrevious")}
                      </Text>
                    </Button>
                    <Text
                      data-testid="partnership-case-count"
                      fontWeight="700"
                      whiteSpace="nowrap"
                    >
                      {t("v2Home.partnership.caseCount", {
                        current: (selectedPartnershipCaseIndex ?? 0) + 1,
                        total: partnershipCases.length,
                      })}
                    </Text>
                    <Button
                      data-testid="partnership-case-next"
                      aria-label={t("v2Home.partnership.caseNext")}
                      onClick={showNextPartnershipCase}
                      minW={{ base: 12, sm: 32 }}
                      minH={12}
                    >
                      <Text display={{ base: "none", sm: "block" }}>
                        {t("v2Home.partnership.caseNext")}
                      </Text>
                      <Text aria-hidden="true" ml={{ base: 0, sm: 2 }}>
                        →
                      </Text>
                    </Button>
                  </Flex>
                </Box>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </SectionShell>

      <SectionShell id="founder-story" tinted>
        <MaskedReveal>
          <SectionHeading
            number="09"
            title={t("v2Home.team.title")}
            statement={t("v2Home.team.statement")}
          />
        </MaskedReveal>
        <Accordion allowToggle>
          {teamItems.map(({ key, image, descriptionKey }) => (
            <AccordionItem key={key} borderColor="blackAlpha.300">
              <h4>
                <AccordionButton px={{ base: 0, md: 4 }} py={6}>
                  <Flex
                    flex="1"
                    align="center"
                    gap={{ base: 4, md: 6 }}
                    textAlign="left"
                  >
                    <Avatar
                      size={{ base: "lg", md: "xl" }}
                      name={t(`v2Home.team.items.${key}.name`)}
                      src={image}
                    />
                    <Box>
                      <Heading
                        as="span"
                        display="block"
                        fontSize={{ base: "lg", md: "2xl" }}
                      >
                        {t(`v2Home.team.items.${key}.name`)}
                      </Heading>
                      <Text color="gray.500" mt={1}>
                        {t(`v2Home.team.items.${key}.role`)}
                      </Text>
                    </Box>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
              </h4>
              <AccordionPanel
                pl={{ base: 0, md: "116px" }}
                pr={{ base: 0, md: 12 }}
                pb={8}
                whiteSpace="pre-line"
                color="gray.600"
                lineHeight="1.9"
              >
                {t(descriptionKey)}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </SectionShell>
    </>
  );
};

export default HomeV2Sections;

